<template>
  <BaseModal
    :value="true"
    @input="$emit('close')"
    title="Address"
    id="infoauto-create-address-cr"
  >
    <div class="flex flex-col p-4">
      <h1 class="txt-lg font-bold mb-4">
        {{ $t("message.address_form.title") }} "{{ customerName }}"
      </h1>
      <form
        @submit.prevent="submit"
        class="flex flex-col gap-4"
        v-if="!loading"
      >
        <div class="form-field">
          <label class="form-label"
            >{{ $t("message.address_form.name") }} *</label
          >
          <input
            :value="name"
            class="regular full bordered bg-gray-100 cursor-not-allowed"
            readonly
          />
          <p class="text-xs text-gray-500 mt-1">
            {{ $t("message.address_form.name_locked_hint") }}
          </p>
        </div>

        <div class="form-field">
          <label class="form-label">{{
            $t("message.address_form.company")
          }}</label>
          <input
            v-model="company"
            :placeholder="$t('message.address_form.company')"
            class="regular full bordered"
          />
        </div>

        <div class="form-field">
          <label class="form-label"
            >{{ $t("message.address_form.country") }} *</label
          >
          <select
            class="regular full bordered"
            v-model="selectedPaisCode"
            @change="onPaisChange"
            required
          >
            <option value="" disabled>{{
              $t("message.address_form.select_country")
            }}</option>
            <option
              v-for="pais in paises"
              :key="pais.codPais"
              :value="pais.codPais"
            >
              {{ pais.nombrePais }}
            </option>
          </select>
        </div>

        <div class="form-field" v-if="selectedPais">
          <label class="form-label"
            >{{ $t("message.address_form.province") }} *</label
          >
          <select
            class="regular full bordered"
            v-model="selectedProvincia"
            @change="onProvinciaChange"
            :disabled="loadingProvincias"
            required
          >
            <option value="" disabled>
              {{
                loadingProvincias
                  ? $t("message.address_form.loading")
                  : $t("message.address_form.select_province")
              }}
            </option>
            <option
              v-for="provincia in provincias"
              :key="provincia.provinciaId"
              :value="provincia.codProvincia"
            >
              {{ provincia.nombreProvincia }}
            </option>
          </select>
        </div>

        <div class="form-field" v-if="selectedProvincia">
          <label class="form-label"
            >{{ $t("message.address_form.canton") }} *</label
          >
          <select
            class="regular full bordered"
            v-model="selectedCanton"
            @change="onCantonChange"
            :disabled="loadingCantones"
            required
          >
            <option value="" disabled>
              {{
                loadingCantones
                  ? $t("message.address_form.loading")
                  : $t("message.address_form.select_canton")
              }}
            </option>
            <option
              v-for="canton in cantones"
              :key="canton.cantonId"
              :value="canton.codCanton"
            >
              {{ canton.nombreCanton }}
            </option>
          </select>
        </div>

        <div class="form-field" v-if="selectedCanton">
          <label class="form-label"
            >{{ $t("message.address_form.district") }} *</label
          >
          <select
            class="regular full bordered"
            v-model="selectedDistrito"
            @change="onDistritoChange"
            :disabled="loadingDistritos"
            required
          >
            <option value="" disabled>
              {{
                loadingDistritos
                  ? $t("message.address_form.loading")
                  : $t("message.address_form.select_district")
              }}
            </option>
            <option
              v-for="distrito in distritos"
              :key="distrito.distritoId"
              :value="distrito.codDistrito"
            >
              {{ distrito.nombreDistrito }}
            </option>
          </select>
        </div>

        <div class="form-field" v-if="selectedDistrito">
          <label class="form-label">{{
            $t("message.address_form.neighborhood")
          }}</label>
          <select
            class="regular full bordered"
            v-model="selectedBarrio"
            @change="onBarrioChange"
            :disabled="loadingBarrios"
          >
            <option value="">
              {{
                loadingBarrios
                  ? $t("message.address_form.loading")
                  : $t("message.address_form.select_neighborhood")
              }}
            </option>
            <option
              v-for="barrio in barrios"
              :key="barrio.barrioId"
              :value="barrio.codBarrio"
            >
              {{ barrio.nombreBarrio }}
            </option>
          </select>
        </div>

        <div class="form-field">
          <label class="form-label"
            >{{ $t("message.address_form.street1") }} *</label
          >
          <input
            v-model="street1"
            :placeholder="$t('message.address_form.street1')"
            class="regular full bordered"
            required
          />
        </div>

        <div class="form-field">
          <label class="form-label">{{
            $t("message.address_form.street2")
          }}</label>
          <input
            v-model="street2"
            class="regular full bordered"
            :placeholder="$t('message.address_form.street2')"
          />
        </div>

        <div class="form-field">
          <label class="form-label"
            >{{ $t("message.address_form.postal_code") }} *</label
          >
          <input
            v-model="zip"
            :placeholder="$t('message.address_form.postal_code')"
            class="regular full bordered"
            required
          />
        </div>

        <div
          v-if="error"
          class="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mt-6"
        >
          {{ error }}
        </div>

        <button type="submit" class="btn-dark-button w-40 mt-6">
          {{ $t("message.address_form.create") }}
        </button>
      </form>
      <div v-else>
        <BaseSmallSpinner />
      </div>
    </div>
  </BaseModal>
</template>

<script>
import {
  addAddressToCustomer,
  getAddressPaises,
  getAddressProvincias,
  getAddressCantones,
  getAddressDistritos,
  getAddressBarrios
} from "@/api/store/Infoauto.js";

export default {
  props: {
    customerName: {
      type: String,
      required: true
    },
    customerInfoautoId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      // Infoauto no guarda nombre por direccion: siempre es el del cliente
      name: this.customerName,
      company: "",
      street1: "",
      street2: "",
      zip: "",
      loading: false,
      error: "",
      // Address hierarchy data
      paises: [],
      provincias: [],
      cantones: [],
      distritos: [],
      barrios: [],
      // Selected values - using codes
      selectedPais: "",
      selectedPaisCode: "CRI",
      selectedProvincia: "",
      selectedProvinciaName: "",
      selectedCanton: "",
      selectedCantonName: "",
      selectedDistrito: "",
      selectedDistritoName: "",
      selectedBarrio: "",
      selectedBarrioName: "",
      // Loading states
      loadingProvincias: false,
      loadingCantones: false,
      loadingDistritos: false,
      loadingBarrios: false
    };
  },
  mounted() {
    this.loadPaises();
  },
  methods: {
    async loadPaises() {
      try {
        const response = await getAddressPaises();
        console.log("Paises response:", response);
        this.paises = Array.isArray(response) ? response : [];

        // Auto-select and load data for the default country (CRI)
        if (this.selectedPaisCode && this.paises.length > 0) {
          const selected = this.paises.find(
            p => p.codPais === this.selectedPaisCode
          );
          if (selected) {
            this.selectedPais = selected.nombrePais;

            // Load provinces for the selected country
            this.loadingProvincias = true;
            try {
              const provincesResponse = await getAddressProvincias(
                this.selectedPais
              );
              console.log("Provincias response:", provincesResponse);
              this.provincias = Array.isArray(provincesResponse)
                ? provincesResponse
                : [];
            } catch (error) {
              console.error("Error loading provinces:", error);
              this.$toast.error("Failed to load provinces");
            } finally {
              this.loadingProvincias = false;
            }
          }
        }
      } catch (error) {
        console.error("Error loading countries:", error);
        this.$toast.error("Failed to load countries");
      }
    },
    async onPaisChange() {
      const selected = this.paises.find(
        p => p.codPais === this.selectedPaisCode
      );
      this.selectedPais = selected?.nombrePais || "";

      this.selectedProvincia = "";
      this.selectedProvinciaName = "";
      this.selectedCanton = "";
      this.selectedCantonName = "";
      this.selectedDistrito = "";
      this.selectedDistritoName = "";
      this.selectedBarrio = "";
      this.selectedBarrioName = "";
      this.provincias = [];
      this.cantones = [];
      this.distritos = [];
      this.barrios = [];

      if (!this.selectedPaisCode) return;

      this.loadingProvincias = true;
      try {
        const response = await getAddressProvincias(this.selectedPais);
        console.log("Provincias response:", response);
        this.provincias = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error("Error loading provinces:", error);
        this.$toast.error("Failed to load provinces");
      } finally {
        this.loadingProvincias = false;
      }
    },
    async onProvinciaChange() {
      const selected = this.provincias.find(
        p => p.codProvincia === this.selectedProvincia
      );
      this.selectedProvinciaName = selected?.nombreProvincia || "";

      this.selectedCanton = "";
      this.selectedCantonName = "";
      this.selectedDistrito = "";
      this.selectedDistritoName = "";
      this.selectedBarrio = "";
      this.selectedBarrioName = "";
      this.cantones = [];
      this.distritos = [];
      this.barrios = [];

      if (!this.selectedProvincia) return;

      this.loadingCantones = true;
      try {
        const response = await getAddressCantones(
          this.selectedProvincia,
          this.selectedPaisCode,
          this.selectedPais
        );
        console.log("Cantones response:", response);
        this.cantones = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error("Error loading cantons:", error);
        this.$toast.error("Failed to load cantons");
      } finally {
        this.loadingCantones = false;
      }
    },
    async onCantonChange() {
      const selected = this.cantones.find(
        c => c.codCanton === this.selectedCanton
      );
      this.selectedCantonName = selected?.nombreCanton || "";

      this.selectedDistrito = "";
      this.selectedDistritoName = "";
      this.selectedBarrio = "";
      this.selectedBarrioName = "";
      this.distritos = [];
      this.barrios = [];

      if (!this.selectedCanton) return;

      this.loadingDistritos = true;
      try {
        const response = await getAddressDistritos(
          this.selectedProvincia,
          this.selectedCanton,
          this.selectedPaisCode,
          this.selectedPais
        );
        console.log("Distritos response:", response);
        this.distritos = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error("Error loading districts:", error);
        this.$toast.error("Failed to load districts");
      } finally {
        this.loadingDistritos = false;
      }
    },
    async onDistritoChange() {
      const selected = this.distritos.find(
        d => d.codDistrito === this.selectedDistrito
      );
      this.selectedDistritoName = selected?.nombreDistrito || "";

      this.selectedBarrio = "";
      this.selectedBarrioName = "";
      this.barrios = [];

      if (!this.selectedDistrito) return;

      this.loadingBarrios = true;
      try {
        const response = await getAddressBarrios(
          this.selectedProvincia,
          this.selectedCanton,
          this.selectedDistrito,
          this.selectedPaisCode,
          this.selectedPais
        );
        console.log("Barrios response:", response);
        this.barrios = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error("Error loading neighborhoods:", error);
        this.$toast.error("Failed to load neighborhoods");
      } finally {
        this.loadingBarrios = false;
      }
    },
    onBarrioChange() {
      const selected = this.barrios.find(
        b => b.codBarrio === this.selectedBarrio
      );
      this.selectedBarrioName = selected?.nombreBarrio || "";
    },
    submit() {
      if (!this.addressIsValid()) {
        return;
      }
      this.error = "";
      this.loading = true;
      console.log("Creating address for customer with Infoauto data:", {
        name: this.name,
        company: this.company,
        street1: this.street1,
        street2: this.street2,
        pais: this.selectedPais,
        cod_provincia: this.selectedProvincia,
        provincia: this.selectedProvinciaName,
        cod_canton: this.selectedCanton,
        canton: this.selectedCantonName,
        cod_distrito: this.selectedDistrito,
        distrito: this.selectedDistritoName,
        cod_barrio: this.selectedBarrio,
        barrio: this.selectedBarrioName,
        postalCode: this.zip
      });

      const newAddress = {
        name: this.name,
        company: this.company,
        street1: this.street1,
        street2: this.street2,
        city: this.selectedDistritoName, // Using distrito name as city
        state: this.selectedProvinciaName, // Using provincia name as state
        postalCode: this.zip,
        country: this.selectedPais, // Using country name
        // Infoauto-specific fields with codes
        cod_pais: this.selectedPaisCode,
        cod_provincia: this.selectedProvincia,
        provincia: this.selectedProvinciaName,
        cod_canton: this.selectedCanton,
        canton: this.selectedCantonName,
        cod_distrito: this.selectedDistrito,
        distrito: this.selectedDistritoName,
        cod_barrio: this.selectedBarrio || "0",
        barrio: this.selectedBarrioName || " "
      };

      addAddressToCustomer(this.customerInfoautoId, newAddress)
        .then(data => {
          console.log("Address created", data.address);
          this.loading = false;
          const addressCreated = {
            // dir_id de Infoauto: para poder guardarla como predeterminada
            infoautoAddressId: data.address.dirId || "",
            name: this.customerName,
            company: this.company || "",
            street1: data.address.calleCasa,
            street2: "",
            street3: "",
            city: data.address.ciudad,
            state: data.address.codEstado,
            postalCode: data.address.codPostal,
            country: data.address.codPais
          };
          this.$emit("addressCreated", addressCreated);
        })
        .catch(error => {
          console.error("Error creating address:", error);
          this.error =
            error.response?.data?.message || "Failed to create address";
          this.$toast.error(this.error);
          this.loading = false;
        });
    },
    addressIsValid() {
      if (this.name === "") {
        this.$toast.error(this.$t("message.address_form.name_required"));
        return false;
      }
      if (this.street1 === "") {
        this.$toast.error(this.$t("message.address_form.address_required"));
        return false;
      }
      if (this.selectedPais === "") {
        this.$toast.error(this.$t("message.address_form.country_required"));
        return false;
      }
      if (this.selectedProvincia === "") {
        this.$toast.error(this.$t("message.address_form.province_required"));
        return false;
      }
      if (this.selectedCanton === "") {
        this.$toast.error(this.$t("message.address_form.canton_required"));
        return false;
      }
      if (this.selectedDistrito === "") {
        this.$toast.error(this.$t("message.address_form.district_required"));
        return false;
      }
      if (this.zip === "") {
        this.$toast.error(this.$t("message.address_form.postal_code_required"));
        return false;
      }
      return true;
    }
  }
};
</script>

<style lang="scss">
#infoauto-create-address-cr {
  .modal-content {
    width: 100%;
    max-width: 600px;
    height: auto;
    max-height: 90vh;
    overflow-y: auto;
  }
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.form-label {
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
}
</style>
